import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Stack, Typography, IconButton,
  Avatar, Divider, Link, Tooltip,
  Paper,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import DownloadIcon from '@mui/icons-material/Download';
import intl from 'components/i18n/ReactIntlWrapper';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { HRApiWrapper } from 'api/HRApiWrapper';
import { WorkflowProvider } from 'components/Workflow/WorkflowContext';
import WorkflowActionsBar from 'components/Workflow/WorkflowActionsBar';
import WorkflowLogEntries from 'components/Workflow/WorkflowLogEntries';
import { WorkflowStatusChip } from 'components/common/WorkflowStatusChip';

interface Props {
  open: boolean;
  onClose: () => void;
  jobOpening: any;
  hrApi: HRApiWrapper;
}

const initials = (name: string, lastName: string) =>
  `${(name?.[0] ?? '').toUpperCase()}${(lastName?.[0] ?? '').toUpperCase()}`;

const ApplicationsDialogInner: React.FC<Props> = ({ open, onClose, jobOpening, hrApi }) => {
  const toast = useToastMessageQueue();
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [errors] = useState({});

  const fetchApplications = useCallback(async () => {
    if (!jobOpening?.id) return;
    setLoading(true);
    try {
      const res = await hrApi.getApplicationsByJobOpening('1', jobOpening.id);
      setApplications(res.data?.data ?? res.data ?? []);
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setLoading(false);
    }
  }, [jobOpening?.id]);

  useEffect(() => {
    if (open && jobOpening?.id) {
      setSelected(null);
      fetchApplications();
    }
  }, [open, jobOpening?.id]);

  const handleTransition = async (newStateId: number) => {
    setSaving(true);
    try {
      setSelected((prev: any) => prev ? { ...prev, idWorkflowState: newStateId } : prev);
      await fetchApplications();
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadResume = async () => {
    setResumeLoading(true);
    try {
      const res = await hrApi.getApplicationResumeUrl('1', selected.id);
      const url = res.data.data;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setResumeLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'candidate', headerName: intl.get('hr.application.candidate'), flex: 1, minWidth: 220,
      renderCell: (params) => {
        const c = params.row.jobCandidate ?? params.row.candidate ?? {};
        return (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ height: '100%' }}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
              {initials(c.name, c.lastName)}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.2 }}>{c.name} {c.lastName}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>{c.email}</Typography>
            </Box>
          </Stack>
        );
      },
    },
    {
      field: 'applicationDate', headerName: intl.get('hr.application.date'), width: 140,
      valueFormatter: (v: any) => v ? new Date(v).toLocaleDateString() : '',
    },
    {
      field: 'idWorkflowState', headerName: intl.get('hr.application.state'), width: 180,
      renderCell: (params) => <WorkflowStatusChip stateId={params.value} />,
    },
    {
      field: 'resumeFileURI', headerName: '', width: 50, sortable: false,
      renderCell: (params) => params.value
        ? <Tooltip title={intl.get('hr.application.resume') || 'Resume'}><DownloadIcon fontSize="small" color="action" /></Tooltip>
        : null,
    },
    {
      field: 'actions', headerName: '', width: 50, sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => setSelected(params.row)}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const renderDetail = () => {
    const c = selected.jobCandidate ?? selected.candidate ?? {};

    return (
      <Box>
        {/* Header row */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            variant="text"
            onClick={() => setSelected(null)}
          >
            {intl.get('common.back') || 'Back'}
          </Button>
          <WorkflowStatusChip stateId={selected.idWorkflowState} size="medium" />
        </Stack>

        {/* Candidate card */}
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar
              sx={{
                width: 56, height: 56, fontSize: 20,
                bgcolor: 'primary.main', flexShrink: 0,
              }}
            >
              {initials(c.name, c.lastName)}
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6" fontWeight={600} mb={0.5}>
                {c.name} {c.lastName}
              </Typography>
              <Stack spacing={0.5}>
                {c.email && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmailIcon fontSize="small" color="action" />
                    <Link href={`mailto:${c.email}`} variant="body2" underline="hover">{c.email}</Link>
                  </Stack>
                )}
                {c.phone && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">{c.phone}</Typography>
                  </Stack>
                )}
                {c.linkedInProfile && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LinkedInIcon fontSize="small" color="action" />
                    <Link href={c.linkedInProfile} target="_blank" rel="noreferrer" variant="body2" underline="hover">
                      {c.linkedInProfile}
                    </Link>
                  </Stack>
                )}
              </Stack>
            </Box>
            {selected.resumeFileURI && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={resumeLoading}
                onClick={handleDownloadResume}
                sx={{ flexShrink: 0 }}
              >
                {resumeLoading ? '...' : (intl.get('hr.application.resume') || 'Resume')}
              </Button>
            )}
          </Stack>
        </Paper>

        {/* Application date + message */}
        <Stack direction="row" spacing={2} mb={selected.remarks ? 2 : 0} flexWrap="wrap">
          {selected.applicationDate && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase">
                {intl.get('hr.application.date') || 'Applied on'}
              </Typography>
              <Typography variant="body2">{new Date(selected.applicationDate).toLocaleDateString()}</Typography>
            </Box>
          )}
        </Stack>

        {selected.remarks && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={500} textTransform="uppercase" display="block" mb={0.5}>
              {intl.get('hr.application.message') || 'Cover message'}
            </Typography>
            <Typography variant="body2">{selected.remarks}</Typography>
          </Paper>
        )}

        {/* Workflow actions */}
        <Divider sx={{ my: 2 }} />
        <WorkflowLogEntries idModule={6} idEntity={selected.id} />
        <Box mt={2} />
        <WorkflowActionsBar
          viewMode="buttons"
          hideCancel
          id={selected.id}
          stateId={selected.idWorkflowState}
          onActionClick={handleTransition}
          loading={saving}
        />
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>{jobOpening?.title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {intl.get('hr.jobOpening.applications') || 'Applications'} · {applications.length}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 420, p: selected ? 3 : 0 }}>
        {selected ? renderDetail() : (
          <DataGrid
            rows={applications}
            columns={columns}
            loading={loading}
            hideFooter={applications.length <= 10}
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            sx={{ border: 'none', '& .MuiDataGrid-cell': { py: 1 } }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3 }}>
        <Button onClick={onClose} variant="outlined">
          {intl.get('common.close') || 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ApplicationsDialog: React.FC<Props> = (props) => (
  <WorkflowProvider moduleId={6}>
    <ApplicationsDialogInner {...props} />
  </WorkflowProvider>
);

export default ApplicationsDialog;
